import axios from '@/util/axios';
import FormErrorMap from '@/store/models/FormErrorMap';

const interviewPath = 'Interview/';

const state = {
	applicationNumber: <string | null> null,
	dropdowns: <any | null> null,
	filingType: <string | null> null,
	formData: <any> {},
	formErrorMap: <FormErrorMap> {},
	isInterviewConfirmed: <boolean> false,
	pageName: <string | null> null,
	resumeSectionName: <string | null> null,
};

const getters = {
	applicationNumber: (state: any) => state.applicationNumber,
	filingType: (state: any) => state.filingType,
	formData: (state: any) => state.formData,
	formErrorMap: (state: any) => state.formErrorMap,
	isInterviewConfirmed: (state: any) => state.isInterviewConfirmed,
	dropdowns: (state: any) => state.dropdowns,
	pageName: (state: any) => state.pageName,
	resumeSectionName: (state: any) => state.resumeSectionName,
};

const mutations = {
	clearFormData: (state: any) => state.formData = {},
	clearFormError: (state: any, fieldId: string) => {
		let formErrorMap = JSON.parse(JSON.stringify(state.formErrorMap));
		if (formErrorMap[fieldId]) delete formErrorMap[fieldId];
		state.formErrorMap = formErrorMap;
	},
	updateApplicationNumber: (state: any, applicationNumber: string) => state.applicationNumber = applicationNumber,
	updateDropdowns: (state: any, value: any) => state.dropdowns = value,
	updateFilingType: (state: any, value: any) => state.filingType = value,
	updateFormData: (state: any, value: any) => state.formData = value || {},
	updateFormDataKey: (state: any, { key, value }: any) => {
		if (key.includes('.') || key.includes('[')) {
			const keyParts = key.split('.');
			let _formData = JSON.parse(JSON.stringify(state.formData));
			let context = _formData;
			for (let i = 0; i < keyParts.length - 1; i++) {
				if (keyParts[i].includes('[')) {
					const index = parseInt(keyParts[i].slice(keyParts[i].indexOf('[') + 1, keyParts[i].length - 1));
					const propName = keyParts[i].split('[')[0];
					if (!context[propName]) context[propName] = [];
					if (!context[propName][index]) context[propName][index] = {};
					context = context[propName][index];
				}
				else {
					if (!context[keyParts[i]]) {
						context[keyParts[i]] = {} ;
						context= context[keyParts[i]];
					}
					else {
						context = context[keyParts[i]]
					};
				}
			}

			context[keyParts[keyParts.length - 1]] = value;
			state.formData = _formData;
		}
		else {
			let _formData = JSON.parse(JSON.stringify(state.formData));
			_formData[key] = value;
			state.formData = _formData;
		}
	},
	updateFormErrorMap: (state: any, value: FormErrorMap) => {
		state.formErrorMap = value;
	},
	updateIsInterviewConfirmed: (state: any, value: boolean) => state.isInterviewConfirmed = value,
	updatePageName: (state: any, value: string) => state.pageName = value,
	updateResumeSectionName: (state: any, value: string) => state.resumeSectionName = value,
};

const actions = {
	async getCountiesForState(context: any, { stateCode, countryCode }: any) {
		try {
			const response = await axios.get(`Address/Counties/${stateCode}/${countryCode}`);
			const dropdowns = context.state.dropdowns;
			dropdowns['County'] = response.data;
			context.commit('updateDropdowns', dropdowns);
		}
		catch (error) {
			context.commit('addErrorMsg', error.toString());
		}
	},
	async getFormData(context: any, pageName: string) {
		try {
			const response = await axios.get/*<InterviewResponse>*/(`${interviewPath}${pageName}?applicationNumber=${context.state.applicationNumber}`);

			const interviewResponse = response.data;
			if (!!interviewResponse) {
				context.commit('updateDropdowns', interviewResponse.Dropdowns);
				context.commit('updateFormData', interviewResponse.ViewModel);
				context.commit('updateFormErrorMap', {});
			}
		}
		catch (error) {
			if (error.response && error.response.data) {
				const formErrorMap: FormErrorMap = error.response.data;
				context.commit('updateFormErrors', formErrorMap);
			}
			console.error(error);
		}
	},
	async getStatesForCountry(context: any, countryCode: string) {
		try {
			const response = await axios.get(`Address/States/${countryCode}`);
			const dropdowns = context.state.dropdowns;
			dropdowns['State'] = response.data;
			context.commit('updateDropdowns', dropdowns);
		}
		catch (error) {
			context.commit('addErrorMsg', error.toString());
		}
	},
	async resumeInterview(context: any, applicationNumber: string) {
		try {
			context.commit('clearFormData');
			context.commit('updateFormErrorMap', {});
			context.commit('updateResumeSectionName', null);

			const response = await axios.get(`Interview/Resume?applicationNumber=${applicationNumber}`);
			const interviewResponse = response.data;
			if (!!interviewResponse) {
				context.commit('updateApplicationNumber', applicationNumber);
				context.commit('updateDropdowns', interviewResponse.Dropdowns);
				context.commit('updateFormData', interviewResponse.ViewModel);
				context.commit('updateResumeSectionName', interviewResponse.ResumeSectionName);
				context.commit('updateFilingType', interviewResponse.FilingType);
			}
		}
		catch (error) {
			console.error(error);
			context.commit('applicationNumber', null);
		}
	},
	async saveAndContinue(context: any) {
		try {
			const response = await axios.post/*<InterviewResponse>*/(`${interviewPath}SaveAndContinue`, {
				applicationNumber: context.state.applicationNumber,
				pageName: context.state.pageName,
				formData: context.state.formData
			});

			const interviewResponse = response.data;
			if (!!interviewResponse) {
				context.commit('updateDropdowns', interviewResponse.Dropdowns);
				context.commit('updateFormData', interviewResponse.ViewModel);
				context.commit('updateFormErrorMap', {});
				if (!!interviewResponse.FilingType) {
					context.commit('updateFilingType', interviewResponse.FilingType);
				}
			}
		}
		catch (error) {
			if (error.response && error.response.data) {
				const formErrorMap: FormErrorMap = error.response.data;
				context.commit('updateFormErrors', formErrorMap);
			}
			console.error(error);
		}
	},
	async startInterview(context: any, options: any) {
		try {
			context.commit('clearFormData');
			context.commit('updateFormErrorMap', {});
			context.commit('updateResumeSectionName', null);

			const response = await axios.get('Interview');
			context.commit('updateApplicationNumber', response.data.ApplicationNumber);
		}
		catch (error) {
			console.error(error);
			context.commit('applicationNumber', null);
		}
	},
};

export default {
	namespaced: true,
	state,
	getters,
	mutations,
	actions,
};
